"use client";
import React from "react";
import ProgramEnrollButton from "@/components/ProgramDetailLayout/templates/components/ProgramEnrollButton";
import { Program } from "@/common/program/types";
import { ProcessedCourse } from "@/common/course/types";
import { ProgramPricingData } from "@/lib/ecommerce/commercetools/client-only/getProgramPricingData";
import ProgramPricing from "@/components/ProgramDetailLayout/templates/components/ProgramPricing";
import { MonarchCurrencySelectorProps } from "@/monarch/types/MonarchCurrencySelector";
import ProgramRetiredMessage from "@/components/ProgramDetailLayout/templates/components/ProgramRetiredMessage";
import {
  PROGRAM_RETIRED_MESSAGE,
  PROGRAM_UNAVAILABLE_MESSAGE,
} from "@/components/ProgramDetailLayout/templates/components/ProgramRetiredMessage/constants";
import {
  ProgramCurrencyInlinePill,
  useProgramCurrencySurfaces,
} from "@/components/ProgramDetailLayout/templates/components/ProgramCurrencySurfaces";
import ActionCardWrapper from "@/components/GeoPricing/ActionCardWrapper";
import { cn } from "@/lib/utils";
import { getProgramPricingAmounts } from "@/lib/currency/getProgramPricingAmounts";
import formatProgramPrice from "@/lib/checkout-geo-pricing/formatProgramPrice";
import {
  DEFAULT_CURRENCY,
  DEFAULT_MULTI_CURRENCY_TOOLTIP,
} from "@/lib/currency/constants";
import { isDisplayOnlyCurrency } from "@/lib/currency/dualPricingRules";
import { useCurrency } from "@/contexts/UserPreferencesContext/hooks/useCurrency";
import Icon from "@/components/icons";
import Tooltip from "@/components/Tooltip";
import useTranslations from "@/i18n/useTranslations";

interface ProgramRightContainerProps {
  programData: Program;
  courses: ProcessedCourse[];
  skus: string[];
  variant?: "light" | "dark";
  pricingData?: Partial<ProgramPricingData> | null;
  programIsEnrollable?: boolean;
  programIsRetired?: boolean;
  currencySelector?: MonarchCurrencySelectorProps;
  isLogoAboveCta?: boolean;
  className?: string;
}

const VARIANT_STYLES = {
  light: {
    bg: "bg-[#F9F8F6]",
    text: "text-[#002626]",
    border: "md:border md:border-[#E7E4DB]",
    padding: "px-8 md:px-4 py-8",
  },
  dark: {
    bg: "bg-[#00262B] md:bg-[#F9F8F6]",
    text: "text-white md:text-[#00262B]",
    border: "border border-[#00262B] md:border-[#F9F8F6]",
    padding: "px-1 md:px-4 py-8",
  },
};

export default function ProgramActionCard({
  programData,
  courses,
  skus,
  variant = "light",
  pricingData,
  programIsEnrollable = true,
  programIsRetired = false,
  currencySelector,
  isLogoAboveCta = false,
  className = "",
}: ProgramRightContainerProps) {
  const tCurrency = useTranslations("Currency");
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.light;
  const { selectedCurrency } = useCurrency();
  const currencySurfaces = useProgramCurrencySurfaces();
  const amounts = getProgramPricingAmounts(pricingData);
  const showUsdReference =
    selectedCurrency !== DEFAULT_CURRENCY &&
    isDisplayOnlyCurrency(selectedCurrency);
  const formattedUsdPrice =
    showUsdReference && amounts?.price
      ? formatProgramPrice(amounts.price, DEFAULT_CURRENCY)
      : null;
  const isGeoPricingEnabled =
    pricingData && pricingData?.geoPricing !== undefined;
  const isUserEligible =
    pricingData?.checkoutGeoPricingEligibility?.eligible === true;
  const boxClassName = `${styles.bg} rounded-xl ${styles.border} ${styles.padding} gap-4 flex flex-col items-center w-full md:w-[356px] ${className}`;

  const showRetiredMessage =
    programIsRetired ||
    Boolean(programData.customizations?.catalogPage?.retiringSoon) ||
    !programData.programEligibleForOneClickPurchase;

  if (showRetiredMessage) {
    const message = programIsRetired
      ? PROGRAM_RETIRED_MESSAGE
      : PROGRAM_UNAVAILABLE_MESSAGE;

    return (
      <div className={boxClassName}>
        <ProgramRetiredMessage textClassName={styles.text} message={message} />
      </div>
    );
  }

  if (programIsEnrollable) {
    return (
      <>
        <ActionCardWrapper
          className={cn(
            boxClassName,
            isGeoPricingEnabled && isUserEligible ? "py-6 md:py-12" : "",
          )}
          pricingData={pricingData}
          badgesPlacement={isLogoAboveCta ? "end" : null}
          location="program-detail-page"
        >
          <div className="flex items-center justify-center gap-4">
            <ProgramPricing
              pricingData={pricingData || {}}
              strikethroughClass="line-through text-gray-500 opacity-50"
              originalPriceClassName={`font-bold ${styles.text}`}
              discountedPriceClassName={`font-bold ${styles.text}`}
              originalPriceSize="lg"
              discountedPriceSize="lg"
            />
          </div>

          <div className="w-full px-8">
            <ProgramEnrollButton
              courses={courses}
              programData={programData}
              skus={skus}
            />
          </div>

          {formattedUsdPrice &&
          (currencySurfaces.hero || currencySurfaces.stickyBar) ? (
            <span className="inline-flex items-center justify-center gap-1 text-sm text-gray-500">
              <span translate="no">
                ≈ {formattedUsdPrice} · {tCurrency("usdReferenceRate")}
              </span>
              <span className="inline-flex items-center">
                <Tooltip
                  content={DEFAULT_MULTI_CURRENCY_TOOLTIP}
                  className="flex items-center"
                >
                  <span className="inline-flex items-center leading-none">
                    <Icon
                      type="info"
                      svgProps={{ className: "h-4 w-4 shrink-0" }}
                    />
                    <span className="sr-only">
                      Currency pricing information
                    </span>
                  </span>
                </Tooltip>
              </span>
            </span>
          ) : null}
        </ActionCardWrapper>

        {currencySurfaces.hero ? (
          <ProgramCurrencyInlinePill
            pricingData={pricingData}
            location="program-pdp-hero"
            currencySelector={currencySelector}
          />
        ) : null}
      </>
    );
  }

  return null;
}
